import React, { useCallback, useState } from 'react';

import { useKitTranslate } from 'common/components/kit-shared/useKitTranslate';
import { showToast } from 'utils/toasts';
import { ToastTypes } from 'common/types/toasts';
import { track } from 'utils/kitAnalytics';
import { useKitWorkspace } from 'containers/KitWorkspaceStorage/context';
import { createKit, patchKit } from 'containers/KitWorkspaceStorage/api';
import type { IKit } from 'containers/KitWorkspaceStorage/types';

import Button from 'common/components/Button';
import { ButtonSchema, ButtonSize } from 'common/components/Button/constants';
import BottomSheet from 'common/components/kit-shared/BottomSheet';
import styles from './KitFormDialog.module.scss';

const NAME_MAX = 60;
const NOTES_MAX = 240;

interface IKitFormDialogProps {
  workspaceKey: string;
  // When set, the dialog edits this kit; otherwise it creates a new one.
  kit?: IKit;
  onCancel: () => void;
  onSaved: (kitId: string) => void;
}

/**
 * Centered kit form dialog, used for both "New kit" (from the Add to kit picker)
 * and "Edit kit" (from a kit card). Kit name is required and unique; notes are
 * optional.
 */
const KitFormDialog = ({
  workspaceKey, kit, onCancel, onSaved,
}: IKitFormDialogProps) => {
  const t = useKitTranslate();
  const { kits, refetch } = useKitWorkspace();
  const isEdit = !!kit;
  const [name, setName] = useState(kit?.name ?? '');
  const [notes, setNotes] = useState(kit?.notes ?? '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const onSubmit = useCallback(async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError(t('texts.kits.newKit.errorRequired'));
      return;
    }
    const dupe = kits.some(
      (k) => k.uuid !== kit?.uuid && (k.name || '').trim().toLowerCase() === trimmed.toLowerCase(),
    );
    if (dupe) {
      setError(t('texts.kits.newKit.errorDuplicate'));
      return;
    }
    setBusy(true);
    try {
      let kitId: string;
      if (kit) {
        await patchKit(kit.uuid, { name: trimmed, notes: notes.trim() });
        kitId = kit.uuid;
        showToast(ToastTypes.success, t('texts.kits.toast.kitUpdated'));
      } else {
        ({ kitId } = await createKit(workspaceKey, { name: trimmed, notes: notes.trim() || undefined }));
        track('kit_created', { workspaceKey, kitId, hasName: true });
        showToast(ToastTypes.success, t('texts.kits.toast.kitCreated'));
      }
      await refetch();
      onSaved(kitId);
    } catch {
      showToast(ToastTypes.error, t('texts.kits.toast.updateError'));
      setBusy(false);
    }
  }, [name, notes, kits, kit, workspaceKey, refetch, onSaved, t]);

  return (
    <BottomSheet
      title={isEdit ? t('texts.kits.newKit.editTitle') : t('texts.kits.newKit.title')}
      subtitle={isEdit ? t('texts.kits.newKit.editSubtitle') : t('texts.kits.newKit.subtitle')}
      onClose={onCancel}
      centered
      closeDisabled={busy}
      footer={(
        <>
          <Button
            schema={ButtonSchema.secondary}
            size={ButtonSize.small}
            onClick={onCancel}
            disabled={busy}
          >
            {t('texts.kits.catalog.cancel')}
          </Button>
          <Button
            schema={ButtonSchema.primary}
            size={ButtonSize.small}
            onClick={onSubmit}
            disabled={busy || !name.trim()}
          >
            {isEdit ? t('texts.kits.newKit.saveBtn') : t('texts.kits.newKit.createBtn')}
          </Button>
        </>
      )}
    >
      <div className={styles.field}>
        <label className={styles.label} htmlFor="kit-form-name">
          {t('texts.kits.newKit.nameLabel')}
          {' '}
          <span className={styles.req}>*</span>
        </label>
        <input
          id="kit-form-name"
          className={styles.input}
          value={name}
          maxLength={NAME_MAX}
          placeholder={t('texts.kits.newKit.namePlaceholder')}
          onChange={(e) => { setName(e.currentTarget.value); setError(''); }}
          disabled={busy}
        />
        <div className={error ? styles.helpError : styles.help}>
          {error || t('texts.kits.newKit.nameHelp')}
        </div>
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="kit-form-notes">
          {t('texts.kits.newKit.notesLabel')}
          {' '}
          <span className={styles.optional}>{t('texts.kits.newKit.notesOptional')}</span>
        </label>
        <textarea
          id="kit-form-notes"
          className={styles.textarea}
          value={notes}
          maxLength={NOTES_MAX}
          placeholder={t('texts.kits.newKit.notesPlaceholder')}
          onChange={(e) => setNotes(e.currentTarget.value)}
          disabled={busy}
        />
      </div>
    </BottomSheet>
  );
};

export default KitFormDialog;
